import { useState, useRef, useEffect } from 'react'
import { X, Send, Loader2, Bot, User, Leaf, ExternalLink } from 'lucide-react'
import './ChatBot.css'

function ChatBot({ crop, onClose }) {
    const [messages, setMessages] = useState([
        {
            role: 'assistant',
            content: `¡Hola! 👋 Soy TerraMind IA, tu asistente agrícola virtual especializado en **${crop || 'agricultura'}** en Colombia.\n\n¿En qué puedo ayudarte hoy? Pregúntame sobre:\n- 🌱 Estado y cuidado del cultivo\n- 💧 Recomendaciones de riego\n- 🦠 Plagas, enfermedades y tratamientos\n- 💰 Análisis financiero y mercado\n- 📊 Interpretación de tus datos`
        }
    ])
    const [input, setInput] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const messagesEndRef = useRef(null)

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }

    useEffect(() => {
        scrollToBottom()
    }, [messages])

    const handleSubmit = async (e) => {
        e.preventDefault()
        if (!input.trim() || isLoading) return

        const userMessage = input.trim()
        setInput('')
        setMessages(prev => [...prev, { role: 'user', content: userMessage }])
        setIsLoading(true)

        try {
            // Get token from localStorage
            const token = localStorage.getItem('terramind_token')

            // Prepare conversation history for context
            const conversationHistory = messages.slice(-6).map(m => ({
                role: m.role,
                content: m.content
            }))

            // Call backend API
            const response = await fetch('/api/api/gemini/chat.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    message: userMessage,
                    crop,
                    history: conversationHistory
                })
            })

            if (!response.ok) {
                throw new Error('Error en la respuesta del servidor')
            }

            const result = await response.json()

            if (result.error) {
                throw new Error(result.error)
            }

            setMessages(prev => [...prev, {
                role: 'assistant',
                content: result.response || result.message
            }])

        } catch (error) {
            console.error('Error calling chat API:', error)

            // Fallback to mock responses
            const response = getMockResponse(userMessage, crop)
            setMessages(prev => [...prev, { role: 'assistant', content: response }])
        } finally {
            setIsLoading(false)
        }
    }

    const getMockResponse = (message, crop) => {
        const lowerMessage = message.toLowerCase()

        if (lowerMessage.includes('riego') || lowerMessage.includes('agua')) {
            return `**Recomendación de Riego para ${crop}:**\n\nBasado en los datos actuales de tu cultivo:\n\n📊 **Humedad del suelo:** 45% (óptimo: 50-70%)\n🌡️ **Evapotranspiración:** 4.2 mm/día\n\n**Recomendación:** Aplicar riego de 15-20 mm en las próximas 24 horas. Preferiblemente en horas de la mañana (6-8 AM) para minimizar la evaporación.\n\n💡 **Tip:** Considera implementar riego por goteo para optimizar el uso del agua en un 30-40%.`
        } else if (lowerMessage.includes('fertiliz') || lowerMessage.includes('nutrient') || lowerMessage.includes('npk')) {
            return `**Estado Nutricional de ${crop}:**\n\n📍 **Zona Norte:**\n- Nitrógeno: 35 mg/kg ⚠️ (Bajo)\n- Fósforo: 28 mg/kg ✅\n- Potasio: 165 mg/kg ✅\n\n**Recomendación de Fertilización:**\n\n1. Aplicar Urea (46-0-0) a dosis de 80 kg/ha\n2. Realizar aplicación foliar con micronutrientes\n3. Repetir análisis en 15 días\n\n⏰ **Mejor momento:** Temprano en la mañana, antes del riego.`
        } else if (lowerMessage.includes('plaga') || lowerMessage.includes('enfermedad') || lowerMessage.includes('insecto')) {
            return `**Monitoreo de Plagas y Enfermedades:**\n\nSegún los últimos análisis en tu cultivo de ${crop}:\n\n🟢 **Riesgo bajo:**\n- No se detectan plagas significativas\n- Sin síntomas de enfermedades foliares\n\n⚠️ **Precaución:**\n- Humedad alta puede favorecer hongos\n- Monitorear presencia de áfidos en brotes nuevos\n\n**Acciones preventivas:**\n1. Mantener buena ventilación del cultivo\n2. Eliminar malezas hospederas\n3. Aplicación preventiva de fungicida cada 15 días`
        } else if (lowerMessage.includes('cosecha') || lowerMessage.includes('producción')) {
            return `**Proyección de Cosecha para ${crop}:**\n\n📅 **Fecha estimada:** 45-60 días\n📊 **GDD acumulados:** 1,250 / 2,000\n\n**Rendimiento proyectado:**\n- Por hectárea: 4.5 ton/ha\n- vs. Meta: +12% 📈\n\n**Factores a optimizar:**\n1. Mantener nutrición balanceada\n2. Control de riego en maduración\n3. Monitorear condiciones climáticas`
        } else if (lowerMessage.includes('precio') || lowerMessage.includes('venta') || lowerMessage.includes('mercado')) {
            return `**Análisis de Mercado para ${crop}:**\n\n📈 **Precios en Corabastos (hoy):**\n- Mínimo: $6,500/kg\n- Máximo: $9,200/kg\n- Promedio: $7,800/kg\n\n📊 **Tendencia:** Al alza (+5% vs. semana anterior)\n\n**Recomendación:**\n- Buen momento para venta\n- Precio por encima del promedio histórico\n- Considerar acuerdos de venta anticipada`
        } else {
            return `Gracias por tu pregunta sobre "${message}".\n\nPara tu cultivo de **${crop}**, te puedo ayudar con:\n\n1. 💧 **Riego:** "¿Cuánto debo regar hoy?"\n2. 🌱 **Fertilización:** "¿Cómo están los nutrientes?"\n3. 🦠 **Sanidad:** "¿Hay riesgo de plagas?"\n4. 📦 **Cosecha:** "¿Cuándo puedo cosechar?"\n5. 💰 **Mercado:** "¿Cómo están los precios?"\n\n¿Sobre cuál tema te gustaría más información?`
        }
    }

    const formatMessage = (content) => {
        // Simple markdown-like formatting
        return content.split('\n').map((line, i) => {
            // Bold text
            line = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
            return <p key={i} dangerouslySetInnerHTML={{ __html: line || '&nbsp;' }} />
        })
    }

    return (
        <div className="chatbot-window glass-card">
            <div className="chat-header">
                <div className="chat-title">
                    <div className="chat-avatar">
                        <Leaf size={20} />
                    </div>
                    <div>
                        <h3>TerraMind IA</h3>
                        <span className="chat-status">
                            {isLoading ? '✍️ Escribiendo...' : '🟢 En línea'}
                        </span>
                    </div>
                </div>
                <button className="chat-close" onClick={onClose}>
                    <X size={20} />
                </button>
            </div>

            <div className="chat-messages">
                {messages.map((msg, index) => (
                    <div key={index} className={`chat-message ${msg.role}`}>
                        <div className="message-avatar">
                            {msg.role === 'assistant' ? <Bot size={16} /> : <User size={16} />}
                        </div>
                        <div className="message-content">
                            {formatMessage(msg.content)}
                        </div>
                    </div>
                ))}
                {isLoading && (
                    <div className="chat-message assistant">
                        <div className="message-avatar"><Bot size={16} /></div>
                        <div className="message-content typing">
                            <span></span><span></span><span></span>
                        </div>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* CTA Banner */}
            <div className="chat-cta">
                <span>¿Te interesa TerraMind para tu finca?</span>
                <a href="https://ia.saineco.com/contacto/" target="_blank" rel="noopener noreferrer">
                    Contáctanos <ExternalLink size={12} />
                </a>
            </div>

            <form className="chat-input-form" onSubmit={handleSubmit}>
                <input
                    type="text"
                    placeholder="Escribe tu pregunta..."
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    disabled={isLoading}
                />
                <button type="submit" disabled={!input.trim() || isLoading}>
                    {isLoading ? <Loader2 size={20} className="animate-spin" /> : <Send size={20} />}
                </button>
            </form>
        </div>
    )
}

export default ChatBot
